import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  GraduationCap, CheckCircle, Loader2, FileText, Shield,
  User, Phone, Mail, AlertCircle, ChevronRight
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const StudentAgreementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agreementData, setAgreementData] = useState(null);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [acknowledgments, setAcknowledgments] = useState({});
  const [signature, setSignature] = useState('');
  
  // For under-18 parent/guardian info
  const [parentInfo, setParentInfo] = useState({
    fullName: '',
    email: '',
    phone: ''
  });
  
  // Get age from user or URL params
  const age = user?.age || parseInt(searchParams.get('age')) || 18;
  const isUnder18 = age < 18;

  useEffect(() => {
    loadAgreement();
  }, [age]);

  const loadAgreement = async () => {
    try {
      // Check if already signed
      if (user) {
        const statusResp = await api.get('/student/agreement/status');
        if (statusResp.data.signed) {
          setAlreadySigned(true);
          setAgreementData(statusResp.data);
          setLoading(false);
          return;
        }
      }
      
      // Get agreement content
      const response = await api.get(`/student/agreement?age=${age}`);
      setAgreementData(response.data);
      
      // Initialize acknowledgments
      const initialAck = {};
      Object.keys(response.data.sections || {}).forEach(key => {
        initialAck[key] = false;
      });
      setAcknowledgments(initialAck);
      
      // Pre-fill parent info if available from user data
      if (user && isUnder18) {
        setParentInfo({
          fullName: user.parent_name || '',
          email: user.parent_email || '',
          phone: user.parent_phone || ''
        });
      }
    } catch (error) {
      console.error('Error loading agreement:', error);
      toast.error('Failed to load agreement');
    } finally {
      setLoading(false);
    }
  };

  const allAcknowledged = Object.values(acknowledgments).every(v => v === true);

  const validateForm = () => {
    if (!allAcknowledged) {
      toast.error('Please acknowledge all sections of the agreement');
      return false;
    }
    if (!signature.trim()) {
      toast.error('Please type your full legal name as signature');
      return false;
    }
    if (isUnder18) {
      if (!parentInfo.fullName.trim()) {
        toast.error('Parent/guardian full name is required');
        return false;
      }
      if (!parentInfo.email.trim()) {
        toast.error('Parent/guardian email is required');
        return false;
      }
      if (!parentInfo.phone.trim()) {
        toast.error('Parent/guardian phone is required');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        email: user?.email || searchParams.get('email'),
        name: user?.name || searchParams.get('name'),
        age: age,
        signer_type: isUnder18 ? 'parent_guardian' : 'self',
        signer_full_name: isUnder18 ? parentInfo.fullName : signature,
        signer_email: isUnder18 ? parentInfo.email : (user?.email || searchParams.get('email')),
        signer_phone: isUnder18 ? parentInfo.phone : null,
        acknowledgments
      };

      let response;
      if (user) {
        // Authenticated endpoint
        response = await api.post('/student/agreement/sign', {
          student_id: user.user_id,
          ...payload
        });
      } else {
        // Public endpoint (during registration)
        response = await api.post('/student/agreement/sign-public', payload);
      }

      toast.success('Agreement signed successfully!');
      if (response.data.email_sent) {
        toast.success(`Confirmation email sent to ${isUnder18 ? parentInfo.email : payload.signer_email}`);
      }

      // Redirect based on context
      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl) {
        navigate(returnUrl);
      } else if (user) {
        navigate('/student');
      } else {
        navigate('/login?registered=true');
      }
    } catch (error) {
      console.error('Error signing agreement:', error);
      toast.error(error.response?.data?.detail || 'Failed to sign agreement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Already signed state
  if (alreadySigned) {
    return (
      <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="student-agreement-signed">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <GraduationCap className="h-12 w-12 text-indigo-700 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Agreement Already Signed</h1>
          </div>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-green-800 mb-2">
                {agreementData?.agreement_type === 'parent_guardian_consent' 
                  ? 'Parent/Guardian Consent Completed' 
                  : 'Student Agreement Completed'}
              </h2>
              <p className="text-green-700 mb-4">
                Signed on {new Date(agreementData?.signed_at).toLocaleDateString()}
              </p>
              <Badge className="bg-green-100 text-green-700">
                Version {agreementData?.version}
              </Badge>
              
              <div className="mt-6">
                <Button onClick={() => navigate('/student')} className="rounded-full">
                  Go to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const sections = agreementData?.sections || {};

  return (
    <div className="min-h-screen bg-stone-50 py-8 px-4" data-testid="student-agreement-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <GraduationCap className="h-12 w-12 text-indigo-700 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {isUnder18 ? 'Parent/Guardian Consent' : 'Student Agreement'}
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            {isUnder18 
              ? 'As the parent or legal guardian, please review and sign the consent form below.'
              : 'Please review and accept our terms of use to continue.'}
          </p>
          <Badge className="mt-4" variant="outline">
            Agreement Version {agreementData?.version}
          </Badge>
        </div>

        {/* Age indicator */}
        <Card className={`mb-6 ${isUnder18 ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              {isUnder18 ? (
                <>
                  <Shield className="h-6 w-6 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Parent/Guardian Consent Required</p>
                    <p className="text-sm text-amber-700">Student is under 18 years old. Parent or guardian must sign.</p>
                  </div>
                </>
              ) : (
                <>
                  <User className="h-6 w-6 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Adult Student Agreement</p>
                    <p className="text-sm text-blue-700">As an adult student (18+), you can sign your own agreement.</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Parent/Guardian Information (for under 18) */}
        {isUnder18 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-indigo-600" />
                Parent/Guardian Information
              </CardTitle>
              <CardDescription>
                Please provide your contact information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="parent-name">Full Legal Name *</Label>
                <Input
                  id="parent-name"
                  value={parentInfo.fullName}
                  onChange={(e) => setParentInfo(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Enter your full legal name"
                  data-testid="parent-name-input"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parent-email">Email Address *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="parent-email"
                      type="email"
                      value={parentInfo.email}
                      onChange={(e) => setParentInfo(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="parent@email.com"
                      className="pl-10"
                      data-testid="parent-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parent-phone">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="parent-phone"
                      type="tel"
                      value={parentInfo.phone}
                      onChange={(e) => setParentInfo(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(123) 456-7890"
                      className="pl-10"
                      data-testid="parent-phone-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agreement Sections */}
        <div className="space-y-4 mb-6">
          {Object.entries(sections).map(([key, section]) => (
            <Card 
              key={key} 
              className={`transition-all ${acknowledgments[key] ? 'border-green-200 bg-green-50/30' : ''}`}
              data-testid={`agreement-section-${key}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  {acknowledgments[key] && <CheckCircle className="h-5 w-5 text-green-600" />}
                  {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 whitespace-pre-line text-sm mb-4">{section.content}</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                  <Checkbox
                    id={`ack-${key}`}
                    checked={acknowledgments[key] || false}
                    onCheckedChange={(checked) => setAcknowledgments(prev => ({ ...prev, [key]: checked }))}
                    data-testid={`ack-checkbox-${key}`}
                  />
                  <label htmlFor={`ack-${key}`} className="text-sm font-medium cursor-pointer">
                    I have read and understand this section
                  </label>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Signature Section */}
        <Card className="border-indigo-200 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Digital Signature
            </CardTitle>
            <CardDescription>
              {isUnder18 
                ? 'Parent/Guardian: By typing your full legal name below, you consent to these terms on behalf of your child.'
                : 'By typing your full legal name below, you agree to all terms and conditions.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signature">
                  Type your full legal name to sign *
                </Label>
                <Input
                  id="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder={isUnder18 ? "Parent/Guardian Full Legal Name" : "Your Full Legal Name"}
                  className="text-lg font-medium"
                  data-testid="signature-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                Acknowledged: {Object.values(acknowledgments).filter(Boolean).length} / {Object.keys(sections).length} sections
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={submitting || !allAcknowledged || !signature.trim()}
            className="rounded-full px-12 py-6 text-lg bg-indigo-600 hover:bg-indigo-700"
            data-testid="sign-agreement-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Signing...
              </>
            ) : (
              <>
                {isUnder18 ? 'Sign Consent Form' : 'Accept & Continue'}
                <ChevronRight className="h-5 w-5 ml-2" />
              </>
            )}
          </Button>
        </div>

        {/* Help Text */}
        <p className="text-center text-sm text-slate-500 mt-4">
          By signing, you confirm that all information provided is accurate and complete.
          A confirmation email will be sent to {isUnder18 ? 'the parent/guardian' : 'your'} email address.
        </p>
      </div>
    </div>
  );
};

export default StudentAgreementPage;
